<script setup lang="ts">
  import { computed, inject, ref } from 'vue'
  import { isAgencyGroupNode } from '@gat-ship/types'
  import { agencyStructureKey } from '@gat-ship/types/injection-keys'
  import {
    ComboboxHeader,
    ComboboxHeaderButton,
    ComboboxHeaderTitle,
    InputGroupCombobox
  } from '@gat-ship/vue-components'
  import { IconCheck } from '@gat-ship/icons'
  import { toSelectItems } from './to-select-items'
  import type { RefDataPickerEmits, RefDataPickerProps } from './picker-props'

  /**
   * An agency group picker that sources agency groups from the injected
   * `agencyStructureKey` provider's structure tree — so items keep their
   * hierarchy `level` for indentation (the flat ref-data list no longer
   * carries it). It composes the borderless `InputGroupCombobox` primitive —
   * it brings no border of its own. Drop it into a `FormField` default slot
   * (like `InputGroupInput` / `CountryPicker`) and it picks up the parent
   * `InputGroup`'s focus / error chrome.
   *
   * Usage:
   * ```vue
   * <FormField :form="form" name="agencyGroupId" label="Agency Group" required>
   *   <template #default="{ field, invalid }">
   *     <AgencyGroupPicker
   *       :id="field.name"
   *       :model-value="field.state.value"
   *       :aria-invalid="invalid"
   *       @update:model-value="field.handleChange"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  defineProps<RefDataPickerProps>()
  defineEmits<RefDataPickerEmits>()

  const agencyStructure = inject(agencyStructureKey)

  const loading = ref(false)
  const fetched = ref(false)

  async function fetchAgencyGroups() {
    if (fetched.value || !agencyStructure) return
    fetched.value = true
    loading.value = true
    try {
      await agencyStructure.fetchAgencyStructure()
    } finally {
      loading.value = false
    }
  }

  // Lazy fetch on first render
  fetchAgencyGroups()

  const agencyGroupItems = computed(() => {
    const groups = (agencyStructure?.agencyStructure.value ?? [])
      .filter(isAgencyGroupNode)
      .filter((g) => !g.isInactive)
    return toSelectItems(groups, {
      value: (g) => g.id,
      title: (g) => g.name,
      search: (g) => g.shortName
    })
  })
</script>

<template>
  <InputGroupCombobox
    :id="id"
    :items="agencyGroupItems"
    :model-value="modelValue"
    :loading="loading"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="ariaInvalid"
    :data-testid="dataTestid"
    :input-class="inputClass"
    @update:model-value="$emit('update:modelValue', $event)"
    @blur="$emit('blur')"
  >
    <template #content-header>
      <ComboboxHeader>
        <ComboboxHeaderTitle>Agency groups</ComboboxHeaderTitle>
        <ComboboxHeaderButton
          v-if="canManage"
          tabindex="-1"
          @click="$emit('manage')"
        >
          Manage
        </ComboboxHeaderButton>
      </ComboboxHeader>
    </template>
    <template #item="{ item, selected }">
      <div
        class="flex flex-1 items-center"
        :style="{ paddingLeft: `${(item.metaData?.level ?? 0) * 8}px` }"
      >
        <span class="flex-1 truncate text-sm">{{ item.title }}</span>
        <span
          v-if="
            item.metaData?.shortName && item.metaData.shortName !== item.title
          "
          class="text-muted-foreground pl-1 text-xs"
        >
          ({{ item.metaData?.shortName }})
        </span>
      </div>
      <IconCheck
        :class="[
          'ml-auto size-3.5 shrink-0',
          selected ? 'opacity-100' : 'opacity-0'
        ]"
      />
    </template>
  </InputGroupCombobox>
</template>
